import { useTranslation } from 'react-i18next';
import { X } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { Badge } from '@/shared/components/ui/badge';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/shared/components/ui/tooltip';
import { getPaletteColor } from '@/core/lib/colors';
import { TagPopoverForm } from './TagPopoverForm';
import type { TemplateTag } from '../types';
import type { TagPopoverFormValues } from '../schemas';

interface TemplateTagsTabProps {
  tags: TemplateTag[];
  editable: boolean;
  onAdd?: (values: TagPopoverFormValues) => void;
  onRemove?: (index: number) => void;
  isPending?: boolean;
}

export function TemplateTagsTab({
  tags,
  editable,
  onAdd,
  onRemove,
  isPending = false,
}: TemplateTagsTabProps) {
  const { t } = useTranslation('projectTemplates');

  return (
    <div className="flex flex-col gap-3" data-testid="project-templates-tags-tab">
      {tags.length === 0 ? (
        <p
          className="py-4 text-center text-sm text-muted-foreground"
          data-testid="project-templates-tags-empty"
        >
          {t('preview.emptyStates.tags')}
        </p>
      ) : (
        <div className="flex flex-wrap gap-2">
          {tags.map((tag, index) => {
            const colorValue = getPaletteColor(tag.color)?.value;
            return (
              <Badge
                key={`${tag.name}-${index}`}
                variant="outline"
                className="gap-1.5 pr-1"
                data-testid={`project-templates-tag-row-${index}`}
              >
                <div
                  className="size-2 rounded-full"
                  style={colorValue ? { background: colorValue } : undefined}
                />
                {tag.name}
                {editable && (
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="size-4"
                        disabled={isPending}
                        aria-label={t('actions.remove')}
                        onClick={() => onRemove?.(index)}
                        data-testid={`project-templates-tag-remove-${index}`}
                      >
                        <X className="size-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('actions.remove')}</TooltipContent>
                  </Tooltip>
                )}
              </Badge>
            );
          })}
        </div>
      )}

      {editable && onAdd && <TagPopoverForm onAdd={onAdd} isPending={isPending} />}
    </div>
  );
}
